/**
 * Rolling log of *real* on-chain touches, built by diffing successive
 * `KothRound` snapshots from `useKothRound` (the same 1 Hz poll that drives the
 * pot + timer). Feeds the diegetic RECENT ACTIVITY board.
 *
 * Models the diff pattern in `watch/useBroadcastEvents.ts` but is its own
 * focused hook: that one emits a single priority event per tick and drops the
 * toucher identity on its `touch` branch, so it can't feed a per-touch ticker.
 *
 * Honesty (this exists *instead* of a fake crowd):
 *  - Every entry is a touch that actually landed on-chain. Nothing is invented;
 *    with zero touches the list is empty and the board says "be the first".
 *  - `seenAtMs` is *local observation time* (Date.now) so "2m ago" is the time
 *    since we saw it — never compared against an on-chain timestamp (the
 *    surfpool clock-drift trap; round-expiry math stays on `useChainNow`).
 *  - On the first snapshot of a round we seed from `round.recentTouchers` (the
 *    on-chain unique-toucher ring, position 0 = current king) so a viewer
 *    joining mid-round sees a populated board. Seeded entries carry neither a
 *    timestamp nor a pot (the ring stores neither) — we don't guess them.
 *  - If >1 touch lands inside one poll we can only attribute the latest toucher
 *    (`currentKing`); we record one entry and never invent the ones we missed.
 */
import { PublicKey } from "@solana/web3.js";
import { useEffect, useRef, useState } from "react";

import type { KothRoundState } from "./useKothRound";

/** One observed touch, derived entirely from on-chain round snapshots. */
export interface TouchEntry {
  /** base58 toucher pubkey (== currentKing at the moment of the touch). */
  toucher: string;
  /** Pot after the touch, in lamports. `null` for ring-seeded entries whose
   *  historical pot the chain does not record. */
  potLamports: bigint | null;
  /** Local wall-clock ms when we first observed the touch. `null` for
   *  ring-seeded entries (the on-chain ring carries no timestamp). */
  seenAtMs: number | null;
  /** Monotonic id for stable React keys / newest-first ordering. */
  seq: number;
}

const MAX_ENTRIES = 12;

export function useTouchActivity(round: KothRoundState | null): TouchEntry[] {
  const [entries, setEntries] = useState<TouchEntry[]>([]);

  const seqRef = useRef(0);
  const roundKeyRef = useRef<string | null>(null);
  const prevTouchRef = useRef<number | null>(null);

  useEffect(() => {
    if (!round) {
      // Round disappeared (not inited / cleared) — drop a stale log.
      if (roundKeyRef.current !== null) {
        roundKeyRef.current = null;
        prevTouchRef.current = null;
        setEntries([]);
      }
      return;
    }

    const roundKey = round.roundId.toString();

    // New round (or first sight): seed from the on-chain ring, emit nothing live.
    if (roundKeyRef.current !== roundKey) {
      roundKeyRef.current = roundKey;
      prevTouchRef.current = round.touchCount;
      const ring = (round.recentTouchers ?? []).filter(
        (pk) => !pk.equals(PublicKey.default),
      );
      // ring[0] is the current king (most recent) → already newest-first.
      const seeded: TouchEntry[] = ring.slice(0, MAX_ENTRIES).map((pk) => ({
        toucher: pk.toBase58(),
        potLamports: null,
        seenAtMs: null,
        seq: (seqRef.current += 1),
      }));
      setEntries(seeded);
      return;
    }

    // Same round: did touch_count advance? Every touch sets king := toucher, so
    // attribute to currentKing and stamp local observation time.
    if (prevTouchRef.current != null && round.touchCount > prevTouchRef.current) {
      const entry: TouchEntry = {
        toucher: round.currentKing.toBase58(),
        potLamports: BigInt(round.potLamports.toString()),
        seenAtMs: Date.now(),
        seq: (seqRef.current += 1),
      };
      setEntries((prev) => [entry, ...prev].slice(0, MAX_ENTRIES));
    }
    prevTouchRef.current = round.touchCount;
  }, [round]);

  return entries;
}
